import {useCallback, useSyncExternalStore} from 'react';
import type {Component, Editor} from 'grapesjs';
import {isUserLoggedIn, isUserPremium} from '@Libraries/user.library';
import {getRemovedImageBackgroundScreenURL} from '@Libraries/user-image-library';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {removeImageBackground} from '@Utils/image.util';
import {openErrorModal} from '@Modals/error-modal';
import {EMAIL_MAKER_PROPS} from '@EmailMakerReact/email-maker-react.constants';
import {getAjaxErrorCode, setImageSrc} from '@EmailMakerReact/lib/email-maker-media-library';
import {getStringProp} from '@EmailMakerReact/lib/email-maker-component-query-library';
import {authenticateUser} from '@EmailMakerReact/lib/email-maker-publish-library';

const MEDIA_STATE_MODEL_KEY = 'pmwEmailMakerMediaState';

const REMOVE_BACKGROUND_LOADING_KEY = 'removingBackground';

interface EmailMakerMediaState {
  removeBackgroundCredits: number;
}

/** Premium background-removal flow with auth and premium gates; re-runs with the original component/element after login. */
export async function removeComponentImageBackground(editor: Editor, component: Component | undefined, el: HTMLElement | undefined): Promise<void> {
  if (!component || !el) {
    return;
  }

  if (!isUserLoggedIn()) {
    authenticateUser(editor, {
      cb: (): void => {
        void removeComponentImageBackground(editor, component, el);
      },
    });
    return;
  }

  if (!isUserPremium()) {
    void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_BG_REMOVALS);
    return;
  }

  await doRemoveBackground(editor, component, el);
}

/** Fetches the team's remaining background-removal credits into per-editor state. */
export async function fetchRemoveBackgroundCredits(editor: Editor): Promise<void> {
  if (!isUserLoggedIn()) {
    setRemoveBackgroundCredits(editor, 0);
    return;
  }

  const credits = (await window.PMW.readLocal('emailmaker/getRemoveBackgroundCredits')) as {removeBGCredits: number};
  setRemoveBackgroundCredits(editor, credits.removeBGCredits);
}

/** The remaining background-removal credits last fetched/decremented. */
export function getRemoveBackgroundCredits(editor: Editor): number {
  const state = editor.getModel().get(MEDIA_STATE_MODEL_KEY) as EmailMakerMediaState | undefined;
  return state?.removeBackgroundCredits ?? 0;
}

/**
 * React hook — returns the live remaining background-removal credit count, re-rendering whenever
 * `doRemoveBackground` or `fetchRemoveBackgroundCredits` writes a new value into the editor model.
 * Returns 0 while the editor is not yet initialised.
 */
export function useRemoveBackgroundCredits(editor: Editor | null): number {
  const subscribe = useCallback(
    (onChange: () => void): (() => void) => {
      if (!editor) {
        return (): void => undefined;
      }
      editor.getModel().on(`change:${MEDIA_STATE_MODEL_KEY}`, onChange);
      return (): void => {
        editor.getModel().off(`change:${MEDIA_STATE_MODEL_KEY}`, onChange);
      };
    },
    [editor]
  );

  const getSnapshot = useCallback((): number => (editor ? getRemoveBackgroundCredits(editor) : 0), [editor]);

  return useSyncExternalStore(subscribe, getSnapshot);
}

/** Runs the removal job and re-resolves the source with the background-removed URL. */
async function doRemoveBackground(editor: Editor, component: Component, el: HTMLElement): Promise<void> {
  const imageHashedFilename = getStringProp(component, EMAIL_MAKER_PROPS.HASHED_FILENAME) ?? '';
  if (!imageHashedFilename) {
    // Surface the error modal but still continue with the removal attempt.
    openErrorModal({
      debugDetails: `No image hashedFilename for component: ${JSON.stringify(component)}`,
    });
  }

  el.classList.add('disabled');
  showLoading(REMOVE_BACKGROUND_LOADING_KEY, {text: window.i18next.t('pmwjs_removing_background')});

  try {
    const response = await removeImageBackground(imageHashedFilename);
    const removedImageBackgroundScreenURL = getRemovedImageBackgroundScreenUrlForEmailEditor(imageHashedFilename);

    setRemoveBackgroundImageToSrc(editor, component, removedImageBackgroundScreenURL);
    if (response) {
      setRemoveBackgroundCredits(editor, response.remainingRemoveBackgroundCredits);
    }
  } catch (error) {
    const errorCode = getAjaxErrorCode(error);
    if (errorCode === 'unprocessable_image') {
      openErrorModal({message: window.i18next.t('pmwjs_remove_bg_unprocessable_image'), log: false});
    } else if (errorCode === 'insufficient_credits') {
      openErrorModal({message: window.i18next.t('pmwjs_remove_bg_failed_insufficient_credits_desc'), log: false});
    } else {
      openErrorModal({message: window.i18next.t('pmwjs_remove_bg_failed_desc'), log: false});
    }
  } finally {
    hideLoading(REMOVE_BACKGROUND_LOADING_KEY);
    el.classList.remove('disabled');
  }
}

/** Forces the png extension (webp throws when cropped) and drops the trailing query the email backend can't handle. */
function getRemovedImageBackgroundScreenUrlForEmailEditor(imageHashedFilename: string): string {
  const removedImageBackgroundScreenURL = getRemovedImageBackgroundScreenURL(imageHashedFilename);
  return `${removedImageBackgroundScreenURL.slice(0, removedImageBackgroundScreenURL.lastIndexOf('.'))}.png`;
}

function setRemoveBackgroundImageToSrc(editor: Editor, component: Component, destUrlScreen: string): void {
  component.set(EMAIL_MAKER_PROPS.REMOVED_BACKGROUND_URL, destUrlScreen);
  void setImageSrc(editor, {
    component,
    source: getStringProp(component, EMAIL_MAKER_PROPS.CONTENT_SOURCE) ?? '',
    hashedFilename: getStringProp(component, EMAIL_MAKER_PROPS.HASHED_FILENAME) ?? '',
  });
}

function setRemoveBackgroundCredits(editor: Editor, removeBackgroundCredits: number): void {
  const state: EmailMakerMediaState = {removeBackgroundCredits};
  editor.getModel().set(MEDIA_STATE_MODEL_KEY, state);
}
